import { Button } from "@albertine/design-system/components/Button";
import { type DialogProps, Drawer } from "vaul";
import LegacyButton from "./Button";
import "./BottomSheet.css";
import { Tag } from "@albertine/design-system/components/Tag";
import classNames from "classnames";
import type { ReactNode } from "react";
import { useBottomSheetContext } from "../context/BottomSheetContext";
import { cn } from "../utils/cn.util";
import Icon from "./Icon";

export function BottomSheet(
    props: DialogProps & {
        children: ReactNode;
        accessibilityLabels: { title: string; description?: string };
        tag?: string;
        action?: {
            text: string;
            onClick: () => void;
            disabled?: boolean;
            extraContent?: ReactNode;
        };
        contentClassName?: string;
        showCloseButton?: boolean;
        wrapperClassName?: string;
    },
) {
    const {
        accessibilityLabels,
        children,
        tag,
        action,
        showCloseButton = true,
        contentClassName,
        wrapperClassName,
        ...rest
    } = props;
    const { bottomSheetIsOpen, openBottomSheet } = useBottomSheetContext();

    return (
        <Drawer.Root open={bottomSheetIsOpen} onOpenChange={openBottomSheet} {...rest}>
            <Drawer.Portal>
                <Drawer.Overlay className="web-lib__bottom-sheet__overlay" />
                <Drawer.Content
                    className={cn("web-lib__bottom-sheet__content", wrapperClassName)}
                >
                    <Drawer.Handle className="web-lib__bottom-sheet__handle" />

                    {tag && (
                        <Tag className="web-lib__bottom-sheet__tag">
                            <span>{tag}</span>
                        </Tag>
                    )}

                    <div
                        className={classNames(
                            contentClassName,
                            "web-lib__bottom-sheet__main-content",
                        )}
                    >
                        <Drawer.Title className="web-lib__bottom-sheet__title">
                            {accessibilityLabels.title}
                        </Drawer.Title>

                        {accessibilityLabels.description && (
                            <Drawer.Description className="web-lib__bottom-sheet__description">
                                {accessibilityLabels.description}
                            </Drawer.Description>
                        )}

                        {children}

                        {showCloseButton && (
                            <Drawer.Close
                                asChild
                                className="web-lib__bottom-sheet__close"
                            >
                                <LegacyButton.Ghost>
                                    <Icon.Close />
                                </LegacyButton.Ghost>
                            </Drawer.Close>
                        )}
                    </div>

                    {action && (
                        <div className="web-lib__bottom-sheet__action grid grid-cols-1 gap-16">
                            {action.extraContent}

                            <Button
                                variant="primary"
                                onPress={action.onClick}
                                isDisabled={action.disabled}
                                className="web-lib__bottom-sheet__action-button w-full"
                            >
                                {action.text}
                            </Button>
                        </div>
                    )}
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    );
}
